import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, Minus, Plus, ArrowLeft, Heart, MapPin, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { getProduct, getFavoriteIds, addFavorite, removeFavorite, getProductReviews } from '../lib/api';
import { useCartStore } from '../lib/cartStore';
import { useAuthStore } from '../lib/authStore';
import { STORE_MAPS_URL } from '../lib/constants';
import { getPixPrice, getInstallments, formatPrice } from '../lib/pricing';
import ProductCard from '../components/product/ProductCard';
import CountdownTimer from '../components/product/CountdownTimer';
import { StarRating, ReviewForm, ReviewCard, RatingSummary } from '../components/product/ReviewSection';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import toast from 'react-hot-toast';

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantidade, setQuantidade] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: 0, review_count: 0, distribution: {} });
  const [showInstallments, setShowInstallments] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getProduct(id);
        setProduct(res.data.product);
        setRelated(res.data.related || []);
        if (isAuthenticated) {
          try {
            const favRes = await getFavoriteIds();
            const ids = favRes.data || [];
            setFavoriteIds(ids);
            setIsFavorite(ids.includes(id));
          } catch(e) {}
        }
        // Load reviews
        try {
          const revRes = await getProductReviews(id);
          setReviews(revRes.data.reviews || []);
          setReviewStats({
            avg_rating: revRes.data.avg_rating || 0,
            review_count: revRes.data.review_count || 0,
            distribution: revRes.data.distribution || {}
          });
        } catch(e) {}
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
    window.scrollTo(0, 0);
    return () => {
      document.title = 'MOTOVELOZ - Peças e Acessórios para Motos';
      const oldMeta = document.querySelector('meta[name="description"]');
      if (oldMeta) oldMeta.remove();
      const oldSchema = document.getElementById('product-schema');
      if (oldSchema) oldSchema.remove();
    };
  }, [id, isAuthenticated]);

  // SEO: atualiza title, meta description e Schema.org quando produto carrega
  useEffect(() => {
    if (!product) return;
    const precoBase = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
    const precoPix = getPixPrice(precoBase);

    // Title
    document.title = `${product.nome} | Peças para Motos | MOTOVELOZ`;

    // Meta description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) { desc = document.createElement('meta'); desc.name = 'description'; document.head.appendChild(desc); }
    desc.content = `${product.nome} - Cód: ${product.codigo}. ${product.categoria} ${product.marca}. A partir de R$ ${formatPrice(precoPix)} no PIX. Compre na MOTOVELOZ - Água Branca, PI.`;

    // Schema.org Product
    let script = document.getElementById('product-schema');
    if (!script) { script = document.createElement('script'); script.id = 'product-schema'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.nome,
      "description": product.descricao || product.nome,
      "sku": product.codigo,
      "image": product.imagemUrl || undefined,
      "brand": { "@type": "Brand", "name": product.marca || "MOTOVELOZ" },
      "offers": {
        "@type": "Offer",
        "url": window.location.href,
        "priceCurrency": "BRL",
        "price": precoPix.toFixed(2),
        "availability": product.estoque > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": { "@type": "Organization", "name": "MOTOVELOZ" }
      },
      ...(reviewStats.review_count > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": reviewStats.avg_rating.toFixed(1),
          "reviewCount": reviewStats.review_count
        }
      } : {})
    });
  }, [product, reviewStats]);

  const handleAddToCart = () => {
    if (!product || product.estoque <= 0) { toast.error('Produto sem estoque'); return; }
    const precoBase = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
    for (let i = 0; i < quantidade; i++) {
      addItem({ id: product.id, codigo: product.codigo, nome: product.nome, preco: precoBase, imagemUrl: product.imagemUrl, estoque: product.estoque });
    }
    toast.success(`${quantidade} item(ns) adicionado(s)!`);
  };

  const handleBuyNow = () => {
    if (!product || product.estoque <= 0) { toast.error('Produto sem estoque'); return; }
    const precoBase = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
    for (let i = 0; i < quantidade; i++) {
      addItem({ id: product.id, codigo: product.codigo, nome: product.nome, preco: precoBase, imagemUrl: product.imagemUrl, estoque: product.estoque });
    }
    navigate('/carrinho');
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) { toast.error('Faça login para favoritar'); return; }
    try {
      if (isFavorite) { await removeFavorite(id); setIsFavorite(false); toast.success('Removido'); }
      else { await addFavorite(id); setIsFavorite(true); toast.success('Favoritado!'); }
    } catch(e) { toast.error('Erro'); }
  };

  const toggleFav = (pid) => {
    setFavoriteIds(prev => prev.includes(pid) ? prev.filter(f => f !== pid) : [...prev, pid]);
  };

  const loadReviews = async () => {
    try {
      const revRes = await getProductReviews(id);
      setReviews(revRes.data.reviews || []);
      setReviewStats({
        avg_rating: revRes.data.avg_rating || 0,
        review_count: revRes.data.review_count || 0,
        distribution: revRes.data.distribution || {}
      });
    } catch(e) {}
  };

  const mapsUrl = STORE_MAPS_URL;

  if (loading) return (
    <div className="min-h-screen flex flex-col bg-[#09090B]"><Header /><main className="flex-1 flex items-center justify-center"><div className="animate-pulse text-zinc-500">Carregando...</div></main><Footer /></div>
  );

  if (!product) return (
    <div className="min-h-screen flex flex-col bg-[#09090B]"><Header /><main className="flex-1 flex items-center justify-center"><p className="text-zinc-500">Produto não encontrado</p></main><Footer /></div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      <Header />
      <main className="flex-1 py-6 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="text-red-500 hover:text-red-400 font-bold flex items-center gap-2 mb-6 text-sm uppercase tracking-wider" data-testid="back-to-products">
            <ArrowLeft className="h-4 w-4" /> Voltar
          </button>

          <div className="bg-zinc-900 border border-zinc-800 overflow-hidden" data-testid="product-detail">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="relative bg-zinc-800">
                {product.isPromocao && (
                  <div className="absolute top-4 right-4 z-10 bg-red-600 text-white text-xs font-bold px-3 py-1.5 uppercase -skew-x-6">Promoção</div>
                )}
                <button onClick={handleFavorite} data-testid="detail-favorite-btn"
                  className={`absolute top-4 left-4 z-10 p-2 rounded-full transition-all ${isFavorite ? 'bg-red-600 text-white' : 'bg-black/60 text-zinc-400 hover:text-red-500'}`}>
                  <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
                <div className="aspect-square">
                  {product.imagemUrl ? (
                    <img src={product.imagemUrl} alt={product.nome} className="w-full h-full object-contain p-6" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Package className="h-32 w-32 text-zinc-700" /></div>
                  )}
                </div>
              </div>

              <div className="p-6 lg:p-10 flex flex-col justify-between">
                <div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2">Cód: {product.codigo}</p>
                  <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-3 sm:mb-4" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="product-name">{product.nome}</h1>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <Link to={`/produtos?categoria=${encodeURIComponent(product.categoria)}`} className="bg-zinc-800 text-zinc-300 px-3 py-1 text-xs uppercase tracking-wider hover:bg-red-600 hover:text-white transition-colors">{product.categoria}</Link>
                    <Link to={`/produtos?marca=${encodeURIComponent(product.marca)}`} className="bg-zinc-800 text-zinc-300 px-3 py-1 text-xs uppercase tracking-wider hover:bg-red-600 hover:text-white transition-colors">{product.marca}</Link>
                  </div>

                  {product.descricao && <p className="text-zinc-400 text-sm mb-4">{product.descricao}</p>}

                  {product.isPromocao && product.promoExpiraEm && (
                    <div className="mb-4 p-3 bg-zinc-800 border border-yellow-600/30">
                      <p className="text-yellow-500 text-xs font-bold uppercase mb-1">Promoção expira em:</p>
                      <CountdownTimer expiresAt={product.promoExpiraEm} />
                    </div>
                  )}

                  <div className="mb-4 sm:mb-6" data-testid="price-section">
                    {(() => {
                      const precoCartao = product.isPromocao && product.precoPromocional ? product.precoPromocional : product.precoVenda;
                      const precoPix = getPixPrice(precoCartao);
                      const parcelas = getInstallments(precoCartao);
                      const maxParcelas = parcelas ? parcelas.parcelas : 0;
                      return (
                        <div>
                          <div className="flex items-baseline gap-2 flex-wrap mb-1">
                            <p className="text-red-500 text-2xl sm:text-3xl md:text-4xl font-black" data-testid="product-price">R$ {formatPrice(precoPix)}</p>
                            <span className="text-zinc-500 text-xs sm:text-sm">(Desconto de 10%) no Pix</span>
                          </div>
                          <p className="text-zinc-400 text-sm sm:text-base mt-1">ou <span className="text-white font-bold">R$ {formatPrice(precoCartao)}</span> no Cartão</p>
                          {parcelas && (
                            <p className="text-zinc-400 text-sm sm:text-base mt-1">{parcelas.parcelas}x de <span className="text-red-500 font-bold">R$ {formatPrice(parcelas.valor)}</span> sem juros no Cartão</p>
                          )}
                          {maxParcelas > 0 && (
                            <div className="mt-2">
                              <button
                                onClick={() => setShowInstallments(!showInstallments)}
                                className="flex items-center gap-1 text-red-500 hover:text-red-400 text-sm font-semibold underline underline-offset-2 transition-colors"
                                data-testid="toggle-installments"
                              >
                                {showInstallments ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                Consulte Opções de Parcelamento
                              </button>
                              {showInstallments && (
                                <div className="mt-3 space-y-1.5 pl-1 border-l-2 border-zinc-800 ml-1" data-testid="installments-list">
                                  {Array.from({ length: maxParcelas }, (_, i) => i + 1).map((n) => (
                                    <p key={n} className="text-zinc-400 text-sm">
                                      {n}x sem juros de <span className="text-white font-bold">R$ {formatPrice(Math.round((precoCartao / n) * 100) / 100)}</span> no Cartão
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <p className="text-sm mb-4 sm:mb-6">
                    Estoque: <span className={`font-bold ${product.estoque > 10 ? 'text-green-500' : product.estoque > 0 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {product.estoque > 0 ? `${product.estoque} disponíveis` : 'Indisponível'}
                    </span>
                  </p>
                </div>

                <div>
                  {product.estoque > 0 && (
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-zinc-400 text-sm font-bold uppercase">Qtd:</span>
                      <button onClick={() => quantidade > 1 && setQuantidade(quantidade - 1)} className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 transition-colors" data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
                      <span className="text-white font-bold w-10 text-center" data-testid="qty-value">{quantidade}</span>
                      <button onClick={() => quantidade < product.estoque && setQuantidade(quantidade + 1)} className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 transition-colors" data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
                    </div>
                  )}

                  <div className="space-y-2 sm:space-y-3 mb-3">
                    <button onClick={handleBuyNow} disabled={product.estoque <= 0} data-testid="buy-now-btn"
                      className="w-full bg-gradient-to-r from-red-600 to-red-400 hover:from-red-700 hover:to-red-500 disabled:bg-zinc-700 disabled:from-zinc-700 disabled:to-zinc-700 disabled:cursor-not-allowed text-white font-bold py-3 sm:py-4 uppercase tracking-wider text-sm sm:text-base transition-all flex items-center justify-center gap-2">
                      <Zap className="h-5 w-5" />
                      Comprar Agora
                    </button>
                    <button onClick={handleAddToCart} disabled={product.estoque <= 0} data-testid="add-to-cart-btn"
                      className="w-full bg-zinc-800 border border-zinc-600 hover:border-red-500 hover:bg-red-600/10 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold py-3 sm:py-4 uppercase tracking-wider text-sm sm:text-base transition-all flex items-center justify-center gap-2">
                      <ShoppingCart className="h-5 w-5" />
                      Adicionar ao Carrinho
                    </button>
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="product-maps-btn"
                      className="w-full flex items-center justify-center gap-2 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 py-3 sm:py-4 text-sm sm:text-base font-bold uppercase tracking-wider transition-colors">
                      <MapPin className="h-5 w-5" /> Comprar na Loja
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <section className="mt-12" data-testid="reviews-section">
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight mb-6" style={{ fontFamily: 'Teko, sans-serif' }}>
              Avaliações
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Summary + Form */}
              <div className="space-y-6">
                <RatingSummary
                  avgRating={reviewStats.avg_rating}
                  reviewCount={reviewStats.review_count}
                  distribution={reviewStats.distribution}
                />
                <ReviewForm productId={id} onSubmit={loadReviews} />
              </div>

              {/* Right: Review list */}
              <div className="lg:col-span-2 space-y-4">
                {reviews.length > 0 ? (
                  reviews.map((r) => (
                    <ReviewCard
                      key={r.id}
                      review={r}
                    />
                  ))
                ) : (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
                    <p className="text-zinc-600 text-sm">Nenhuma avaliação ainda. Seja o primeiro!</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight mb-8" style={{ fontFamily: 'Teko, sans-serif' }}>Relacionados</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {related.map((p) => <ProductCard key={p.id} product={p} isFavorite={favoriteIds.includes(p.id)} onFavoriteToggle={toggleFav} />)}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
